import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Mail, ArrowLeft, ArrowRight, Shield, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendLink = async () => {
    if (!email.trim()) {
      toast.error("Veuillez entrer votre email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/request`, { email: email.trim() });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du lien");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSendLink();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 top-[18%] h-[380px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 via-indigo-600/15 to-violet-600/20 blur-[100px]"></div>
        <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,#000_20%,transparent_70%)]"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <a href="/" className="mb-7 flex items-center justify-center gap-2.5">
          <img src="/images/logo-navbar.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-white/10" />
          <span className="font-heading text-lg font-bold tracking-tight text-white">VantaPulse</span>
        </a>
        <div className="bg-[#0f1019]/90 backdrop-blur-2xl border border-white/[0.09] rounded-[26px] p-8 sm:p-9 shadow-[0_40px_120px_-30px_rgba(59,130,246,0.45)] relative overflow-hidden animate-slideUp">
          {!sent ? (
            <>
              <div className="text-center mb-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 to-violet-500/15 blur-3xl rounded-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-blue-900/40 ring-1 ring-white/10">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading text-[26px] font-bold tracking-tight text-white mb-2">
                    Mot de passe oublié ?
                  </h2>
                  <p className="text-white/50 text-sm">Recevez un lien de réinitialisation par email</p>
                </div>
              </div>

              <div className="mb-6 group">
                <label className="block text-[13px] text-white/75 mb-1.5 font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="votre@email.com"
                    autoFocus
                    className="relative w-full pl-12 pr-5 py-3.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => navigate("/")}
                  className="flex-1 py-3.5 bg-white/[0.04] hover:bg-white/[0.08] text-white/85 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 border border-white/[0.08]"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Retour
                </button>
                <button
                  onClick={handleSendLink}
                  disabled={loading}
                  className="flex-1 py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:brightness-110 active:scale-[0.99] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)]"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Envoi...
                    </>
                  ) : (
                    <>
                      Envoyer le lien
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center space-y-6 animate-slideUp">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 to-emerald-500/30 blur-3xl rounded-full animate-pulse"></div>
                <div className="relative w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-green-500/30">
                  <MailCheck className="w-10 h-10 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Vérifiez votre boîte mail</h2>
                <p className="text-white/50">
                  Si un compte existe pour <span className="text-white/80">{email}</span>, un lien pour modifier
                  votre mot de passe vient d'être envoyé. Le lien expire dans 30 minutes.
                </p>
              </div>
              <button
                onClick={() => setSent(false)}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
              >
                Renvoyer le lien
              </button>
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .animate-slideUp {
            animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        `}</style>
      </div>
    </div>
  );
}
